/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IArticleTagResult } from '../../../actions/get-article-tags.action';

export interface IArticleTagRefinerProps {
    /** All selectable tags with their article counts. */
    tags: IArticleTagResult[];
    /** Currently selected tags (single-select — at most one). */
    selectedTags: string[];
    /** Heading shown above the tag list. */
    title: string;
    /** Label for the clear/show-all control. */
    clearAllLabel: string;
    /** Invoked when a tag is clicked (selects it, replacing any previous selection). */
    onSelect(tag: string): void;
    /** Invoked when the clear/show-all control is activated. */
    onClearAll(): void;
}

/**
 * Presentational tag refiner panel (single-select). Stateless — selection state lives in the parent's listPageState.
 * No selection means "show all"; clicking a value filters by it; clicking the active value clears it.
 */
const ArticleTagRefiner: React.FC<IArticleTagRefinerProps> = ({ tags, selectedTags, title, clearAllLabel, onSelect, onClearAll }) => {
    if (!tags || tags.length === 0) {
        return null;
    }

    const hasSelection = selectedTags.length > 0;

    return (
        <div className='lobasArticleCollection__refiner'>
            <div className='lobasArticleCollection__refiner-header'>
                <h3 className='lobasArticleCollection__refiner-title'>{title}</h3>
                {hasSelection && (
                    <button type='button' className='lobasArticleCollection__refiner-clear' onClick={onClearAll}>
                        {clearAllLabel}
                    </button>
                )}
            </div>
            <ul className='lobasArticleCollection__refiner-list'>
                {tags.map(({ tag, count }) => {
                    const isActive = selectedTags.includes(tag);
                    const itemClassName = `lobasArticleCollection__refiner-item${
                        isActive ? ' lobasArticleCollection__refiner-item--active' : ''
                    }`;
                    return (
                        <li key={tag} className={itemClassName}>
                            <button
                                type='button'
                                className='lobasArticleCollection__refiner-button'
                                aria-pressed={isActive}
                                onClick={() => onSelect(tag)}
                            >
                                <span className='lobasArticleCollection__refiner-tag'>{tag}</span>
                                <span className='lobasArticleCollection__refiner-count'>{`(${count})`}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default ArticleTagRefiner;
